import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { uid } from '../../lib/storage';
import { useLiveTickers, useTickerStore } from '../../lib/tickers';
import { tickerOf } from '../../lib/symbols';
import type { SymbolRef } from '../../lib/symbols';
import { formatPrice } from '../../lib/format';
import { toast } from '../../components/ui/Toast';
import { t } from '../../lib/i18n';

export type AlertCondition = 'crossing' | 'above' | 'below';

export interface PriceAlert {
  id: string;
  exchangeId: string;
  pair: string;
  condition: AlertCondition;
  price: number;
  note: string;
  createdAt: number;
  active: boolean;
  triggeredAt?: number;
  /** Last price seen while armed (crossing detection). */
  lastPrice?: number;
}

export interface AlertLogEntry {
  id: string;
  alertId: string;
  at: number;
  price: number;
  text: string;
}

interface AlertsState {
  alerts: PriceAlert[];
  log: AlertLogEntry[];
  add: (alert: Omit<PriceAlert, 'id' | 'createdAt' | 'active'>) => PriceAlert;
  update: (id: string, patch: Partial<PriceAlert>) => void;
  remove: (id: string) => void;
  clearLog: () => void;
}

const ALERTS_KEY = 'ch:alerts';

/**
 * Re-read the shared alert list from localStorage into this tab's store (localStorage is synchronous,
 * so this completes immediately). Every mutation calls it first, so a tab never writes a stale copy
 * over another tab's newer change.
 */
export function syncAlertsFromStorage(): void {
  // Without usable storage persist never attaches its API; alerts then just live in memory.
  try {
    void useAlerts.persist?.rehydrate();
  } catch {
    // Storage unreadable: keep this tab's in-memory list.
  }
}

export const useAlerts = create<AlertsState>()(
  persist(
    (set, get) => ({
      alerts: [],
      log: [],
      add: (alert) => {
        syncAlertsFromStorage();
        const created: PriceAlert = { ...alert, id: uid('a'), createdAt: Date.now(), active: true };
        set({ alerts: [created, ...get().alerts] });
        return created;
      },
      update: (id, patch) => {
        syncAlertsFromStorage();
        set({ alerts: get().alerts.map((a) => (a.id === id ? { ...a, ...patch } : a)) });
      },
      remove: (id) => {
        syncAlertsFromStorage();
        set({ alerts: get().alerts.filter((a) => a.id !== id) });
      },
      clearLog: () => {
        syncAlertsFromStorage();
        set({ log: [] });
      },
    }),
    { name: ALERTS_KEY },
  ),
);

export function describeAlert(alert: Pick<PriceAlert, 'pair' | 'condition' | 'price'>): string {
  const key = alert.condition === 'above' ? 'alerts.describe.above' : alert.condition === 'below' ? 'alerts.describe.below' : 'alerts.describe.crossing';
  return t(key, { ticker: tickerOf(alert.pair), price: formatPrice(alert.price) });
}

function beep(): void {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    osc.onended = () => void ctx.close();
  } catch {
    // Audio blocked until the page has had a user gesture; the toast still shows.
  }
}

/** Ask once for browser notifications (only after the user creates an alert). */
export async function ensureNotificationPermission(): Promise<void> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
  try {
    await Notification.requestPermission();
  } catch {
    // Some browsers only allow the prompt from a click handler; alerts still toast.
  }
}

function fire(alert: PriceAlert, price: number): void {
  // Claim the alert: only the tab that still sees it armed in shared storage may notify. The claim
  // (active:false) is written synchronously below, before any side effect.
  syncAlertsFromStorage();
  const fresh = useAlerts.getState().alerts.find((a) => a.id === alert.id);
  if (!fresh?.active) return;
  const text = `${t('alerts.fired', { alert: describeAlert(alert), price: formatPrice(price) })}${alert.note ? `. ${alert.note}` : ''}`;
  useAlerts.setState((s) => ({
    alerts: s.alerts.map((a) => (a.id === alert.id ? { ...a, active: false, triggeredAt: Date.now(), lastPrice: price } : a)),
    log: [{ id: uid('g'), alertId: alert.id, at: Date.now(), price, text }, ...s.log].slice(0, 100),
  }));
  toast(t('alerts.firedToast'), { body: text, tone: 'warn', duration: 0 });
  beep();
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      new Notification(t('alerts.notificationTitle'), { body: text, tag: alert.id });
    } catch {
      // Notification constructor unavailable (e.g. some mobile browsers).
    }
  }
}

/** Evaluate armed alerts on every ticker update. Returns the unsubscribe function. */
export function startAlertWatcher(): () => void {
  // Previous price per alert as seen by THIS tab's stream. The persisted lastPrice is shared, so
  // another tab's write must not replace it: that could hide a crossing this tab observed.
  const localPrev = new Map<string, number>();
  // Forget the baseline of any alert that is no longer armed (fired here or in another tab, deleted),
  // even when no ticker callback arrives any more, so a later re-arm starts with a fresh baseline.
  const stopPruning = useAlerts.subscribe((s) => {
    for (const id of [...localPrev.keys()]) {
      if (!s.alerts.some((a) => a.id === id && a.active)) localPrev.delete(id);
    }
  });
  const stopTickers = useTickerStore.subscribe((state) => {
    const current = useAlerts.getState().alerts;
    for (const alert of current) {
      if (!alert.active) continue;
      const ticker = state.byKey[`${alert.exchangeId}:${alert.pair}`];
      if (!ticker || !Number.isFinite(ticker.last)) continue;
      const price = ticker.last;
      const prev = localPrev.get(alert.id) ?? alert.lastPrice;
      localPrev.set(alert.id, price);
      const hit =
        alert.condition === 'above'
          ? price >= alert.price
          : alert.condition === 'below'
            ? price <= alert.price
            : prev !== undefined && (prev - alert.price) * (price - alert.price) <= 0 && prev !== price;
      if (hit) fire(alert, price);
      else if (alert.lastPrice !== price) useAlerts.getState().update(alert.id, { lastPrice: price });
    }
  });
  return () => {
    stopPruning();
    stopTickers();
  };
}

/** Pick up alert changes made by other tabs (the `storage` event only fires in the other tabs). */
export function listenForAlertChanges(): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key !== ALERTS_KEY && e.key !== null) return;
    let gone = false;
    try {
      gone = localStorage.getItem(ALERTS_KEY) === null;
    } catch {
      return;
    }
    // Key removed (storage cleared in another tab): persist's merge would keep our stale copy.
    if (gone) useAlerts.setState({ alerts: [], log: [] });
    else syncAlertsFromStorage();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Watches live prices for every armed alert while the site is open. Mount once. */
export function useAlertEngine(): void {
  const alerts = useAlerts((s) => s.alerts);
  const armed = alerts.filter((a) => a.active);
  const refs: SymbolRef[] = armed.map((a) => ({ exchangeId: a.exchangeId, pair: a.pair }));
  useLiveTickers(refs);

  useEffect(() => {
    const stopWatching = startAlertWatcher();
    const stopListening = listenForAlertChanges();
    return () => {
      stopWatching();
      stopListening();
    };
  }, []);
}
